import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Inbox, Plus, Clock, CheckCircle, AlertTriangle, 
  Wrench, CreditCard, Home, Shield, HelpCircle, 
  Loader2, Send, ArrowLeft, User, X, Filter,
  FileText, Calendar, Building, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

/**
 * My Cases - Unified Case Management Interface
 * All case types (maintenance, support, disputes, etc.) managed in one place.
 * Updated to use the unified /api/cases endpoint.
 */
export default function MyCases() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCase, setActiveCase] = useState(null);
  const [showNewCase, setShowNewCase] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState(null);
  
  // New case form
  const [formData, setFormData] = useState({
    case_type: '',
    title: '',
    description: '',
    priority: 'medium',
    maintenance_category: ''
  });

  // Case type categories aligned with unified cases system
  const caseTypes = [
    { value: 'maintenance', label: 'Maintenance', icon: Wrench, description: 'Report a repair or maintenance problem', category: 'maintenance' },
    { value: 'emergency_repair', label: 'Emergency Repair', icon: AlertTriangle, description: 'Urgent repairs needed immediately', category: 'maintenance' },
    { value: 'rent_arrears', label: 'Rent & Payments', icon: CreditCard, description: 'Payment queries, arrears, or billing issues', category: 'arrears' },
    { value: 'deposit_claim', label: 'Deposit Query', icon: Shield, description: 'Questions about your deposit', category: 'dispute' },
    { value: 'complaint', label: 'Complaint', icon: AlertTriangle, description: 'Lodge a formal complaint', category: 'dispute' },
    { value: 'dispute', label: 'Dispute', icon: FileText, description: 'Raise a formal dispute', category: 'dispute' },
    { value: 'support', label: 'General Support', icon: HelpCircle, description: 'Other questions or requests', category: 'support' },
  ];

  const maintenanceCategories = [
    'plumbing', 'electrical', 'heating', 'appliances', 
    'security', 'exterior', 'interior', 'pest', 'cleaning', 'garden', 'other'
  ];

  useEffect(() => {
    loadCases();
    loadStats();
  }, [activeTab]);

  const loadCases = async () => {
    try {
      setLoading(true);
      // Use unified cases API with category filter
      let endpoint = '/cases?include_closed=true';
      if (activeTab !== 'all') {
        endpoint += `&category=${activeTab}`;
      }
      const { data } = await api.get(endpoint);
      setCases(data.cases || []);
    } catch (error) {
      console.error('Failed to load cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/cases/stats/summary');
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const createCase = async (e) => {
    e.preventDefault();
    if (!formData.case_type || !formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      const payload = {
        case_type: formData.case_type,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
      };

      // Add maintenance category if it's a maintenance type
      if (['maintenance', 'emergency_repair', 'routine_repair'].includes(formData.case_type)) {
        payload.maintenance_category = formData.maintenance_category || 'other';
      }

      const { data } = await api.post('/cases', payload);

      toast.success('Case submitted successfully. Our team will respond shortly.');
      
      setCases(prev => [data, ...prev]);
      setActiveCase(data);
      setShowNewCase(false);
      setFormData({ case_type: '', title: '', description: '', priority: 'medium', maintenance_category: '' });
      loadStats();

    } catch (error) {
      console.error('Create case error:', error);
      toast.error('Failed to submit case. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const addTimelineEntry = async () => {
    if (!activeCase || !newMessage.trim()) return;

    setSending(true);
    try {
      await api.post(`/cases/${activeCase.id}/timeline`, {
        event_type: 'message',
        title: 'Message from user',
        description: newMessage
      });

      // Reload the case to get updated timeline
      const { data } = await api.get(`/cases/${activeCase.id}?include_timeline=true`);
      setActiveCase(data);
      
      // Update in cases list
      setCases(prev => prev.map(c => c.id === activeCase.id ? data : c));
      setNewMessage('');
      toast.success('Message sent');

    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const resolveCase = async () => {
    if (!activeCase) return;

    try {
      const { data } = await api.post(`/cases/${activeCase.id}/resolve`, {
        status: 'resolved',
        resolution_type: 'user_closed',
        resolution_summary: 'Closed by user'
      });
      
      setActiveCase(data);
      setCases(prev => prev.map(c => c.id === activeCase.id ? data : c));
      loadStats();
      toast.success('Case closed');
    } catch (error) {
      toast.error('Failed to close case');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'open': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Open' },
      'under_review': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Under Review' },
      'in_progress': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'In Progress' },
      'scheduled': { bg: 'bg-cyan-100', text: 'text-cyan-800', label: 'Scheduled' },
      'awaiting_response': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Awaiting Response' },
      'resolved': { bg: 'bg-green-100', text: 'text-green-800', label: 'Resolved' },
      'closed': { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Closed' },
      'escalated': { bg: 'bg-red-100', text: 'text-red-800', label: 'Escalated' },
    };
    const style = styles[status] || styles['open'];
    return (
      <Badge className={`${style.bg} ${style.text} hover:${style.bg}`}>
        {style.label}
      </Badge>
    );
  };

  const getCaseIcon = (type) => {
    const icons = {
      'maintenance': Wrench,
      'emergency_repair': AlertTriangle,
      'routine_repair': Wrench,
      'rent_arrears': CreditCard,
      'deposit_claim': Shield,
      'dispute': FileText,
      'complaint': AlertTriangle,
      'support': HelpCircle,
      'damage': AlertTriangle,
      'eviction': Home,
    };
    return icons[type] || HelpCircle;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) {
      return date.toLocaleDateString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      'critical': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800',
    };
    return <Badge className={styles[priority] || styles.medium}>{priority}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl" data-testid="my-cases-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">My Cases</h1>
            <p className="text-slate-600">View and manage all your cases in one place</p>
          </div>
          {!showNewCase && !activeCase && (
            <Button onClick={() => setShowNewCase(true)} className="gap-2" data-testid="raise-case-btn">
              <Plus className="w-4 h-4" />
              Raise a Case
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && !showNewCase && !activeCase && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Open Cases</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total_open || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Resolved</p>
                <p className="text-2xl font-bold text-green-600">{stats.total_resolved || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Maintenance</p>
                <p className="text-2xl font-bold text-amber-600">
                  {(stats.by_type?.maintenance || 0) + (stats.by_type?.emergency_repair || 0) + (stats.by_type?.routine_repair || 0)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Support</p>
                <p className="text-2xl font-bold text-purple-600">{stats.by_type?.support || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* New Case Form */}
        {showNewCase && (
          <Card className="mb-6" data-testid="new-case-form">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Raise a New Case</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowNewCase(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={createCase} className="space-y-6">
                {/* Case Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    What type of case is this? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {caseTypes.map((type) => {
                      const Icon = type.icon;
                      const isSelected = formData.case_type === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, case_type: type.value }))}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            isSelected 
                              ? 'border-primary bg-primary/5' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`case-type-${type.value}`}
                        >
                          <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-primary' : 'text-slate-400'}`} />
                          <p className={`font-medium text-sm ${isSelected ? 'text-primary' : 'text-slate-900'}`}>
                            {type.label}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Maintenance Category (conditional) */}
                {['maintenance', 'emergency_repair', 'routine_repair'].includes(formData.case_type) && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {maintenanceCategories.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, maintenance_category: cat }))}
                          className={`px-3 py-2 rounded-full text-sm transition-all ${
                            formData.maintenance_category === cat
                              ? 'bg-primary text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Brief summary of your issue"
                    maxLength={100}
                    data-testid="case-title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Please provide as much detail as possible about your issue."
                    rows={5}
                    data-testid="case-description-input"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Priority
                  </label>
                  <div className="flex gap-3">
                    {[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' },
                      { value: 'critical', label: 'Critical' }
                    ].map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, priority: p.value }))}
                        className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                          formData.priority === p.value
                            ? 'border-primary bg-primary/5'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <p className={`font-medium text-sm ${formData.priority === p.value ? 'text-primary' : 'text-slate-900'}`}>
                          {p.label}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button type="submit" disabled={sending} className="flex-1" data-testid="submit-case-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Submit Case
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowNewCase(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Case Detail View */}
        {activeCase && !showNewCase && (
          <Card className="mb-6" data-testid="case-detail-view">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => setActiveCase(null)} data-testid="back-to-list-btn">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <CardTitle className="text-lg">{activeCase.title}</CardTitle>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {getStatusBadge(activeCase.status)}
                      {getPriorityBadge(activeCase.priority)}
                      <span className="text-xs text-slate-500">
                        {activeCase.case_number}
                      </span>
                      <span className="text-xs text-slate-500">
                        • {formatDate(activeCase.created_at)}
                      </span>
                      {activeCase.maintenance_category && (
                        <Badge variant="outline">{activeCase.maintenance_category}</Badge>
                      )}
                    </div>
                  </div>
                </div>
                {!['closed', 'resolved'].includes(activeCase.status) && (
                  <Button variant="outline" size="sm" onClick={resolveCase} data-testid="close-case-btn">
                    Close Case
                  </Button>
                )}
              </div>
            </CardHeader>
            
            {/* Case Details */}
            <CardContent className="p-4 border-b bg-slate-50">
              <p className="text-sm text-slate-700">{activeCase.description}</p>
              {activeCase.sla_deadline && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">SLA Deadline: {formatDate(activeCase.sla_deadline)}</span>
                  {activeCase.sla_breached && <Badge className="bg-red-100 text-red-800">SLA Breached</Badge>}
                </div>
              )}
            </CardContent>

            {/* Timeline */}
            <CardContent className="p-0">
              <div className="p-4 border-b bg-slate-100">
                <h3 className="font-medium text-sm text-slate-700">Timeline</h3>
              </div>
              <div className="divide-y max-h-80 overflow-y-auto">
                {activeCase.timeline?.length > 0 ? (
                  activeCase.timeline.map((entry, idx) => (
                    <div key={idx} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-slate-100">
                          {entry.event_type === 'message' ? (
                            <MessageSquare className="w-4 h-4 text-slate-600" />
                          ) : entry.event_type === 'created' ? (
                            <Plus className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-slate-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{entry.title}</span>
                            <span className="text-xs text-slate-400">{formatDate(entry.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600">{entry.description}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No timeline entries yet</p>
                  </div>
                )}
              </div>
              
              {/* Reply Input */}
              {!['closed', 'resolved'].includes(activeCase.status) && (
                <div className="p-4 border-t bg-white">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Add a note or message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && addTimelineEntry()}
                      data-testid="case-message-input"
                    />
                    <Button onClick={addTimelineEntry} disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cases List */}
        {!showNewCase && !activeCase && (
          <>
            {/* Tabs for filtering */}
            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="mb-4">
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all">All Cases</TabsTrigger>
                <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
                <TabsTrigger value="dispute" data-testid="tab-disputes">Disputes</TabsTrigger>
                <TabsTrigger value="support" data-testid="tab-support">Support</TabsTrigger>
              </TabsList>
            </Tabs>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : cases.length === 0 ? (
              <Card className="p-12 text-center">
                <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">No cases yet</h3>
                <p className="text-slate-500 mb-4">You haven't raised any cases. Need help?</p>
                <Button onClick={() => setShowNewCase(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Raise Your First Case
                </Button>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="cases-list">
                {cases.map((caseItem) => {
                  const CaseIcon = getCaseIcon(caseItem.case_type);
                  return (
                    <Card 
                      key={caseItem.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setActiveCase(caseItem)}
                      data-testid={`case-card-${caseItem.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <CaseIcon className="w-5 h-5 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-slate-900 truncate">{caseItem.title}</h3>
                              {getStatusBadge(caseItem.status)}
                              {getPriorityBadge(caseItem.priority)}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span>{caseItem.case_number}</span>
                              <span>•</span>
                              <span className="capitalize">{caseItem.case_type?.replace('_', ' ')}</span>
                              <span>•</span>
                              <span>{formatDate(caseItem.created_at)}</span>
                            </div>
                          </div>
                          <ArrowLeft className="w-5 h-5 text-slate-400 rotate-180" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
